import {
    destroyRoomState,
    getRoomState,
    setRoomIDState,
    updateRoomState,
} from '../app-state/state.room'
import { getScreenState, setScreenOrientation } from '../app-state/state.screen'
import { RoomAction, SettingsType } from '../constants/enums'
import {
    addToLoungeRecentModelsSession,
    calculateLoungeSettings,
} from '../mfcLogic/lounge'
import { forgetRoom, setPinnedRoomSession } from '../mfcLogic/rooms'
import { MODALSTATE, closeFloatingPreview } from '../state/modals'
import { closeFloatingRoom } from '../state/modals/state.floater'
import { ROOMSTATE, roomIntervals, setPinnedRooms } from '../state/state.rooms'
import { SETTINGSSTATE } from '../state/state.settings'
import { toggleSidebarVisibility } from '../state/state.sidebar'
import { getSlugAndId, idToSlug, slugToID } from '../state/state.users'
import { initVideo } from '../state/state.video'
import { routerPush } from '../utilities/router'
import { type TransitionType } from '../utilities/transitions'
import { getSafeNextOrPreviousSurrogate } from './getNextOrPreviousModel'
import { loadUserIfNeeded } from './loadUserIfNeeded'
import { setLoungeSurrogateURL } from './nav'
import { saveSettings } from './settings'
import { roomAction } from './workerActions'

export const openRoomState = async (
    slugOrId: string | number,
    options: {
        forceSub?: boolean
        pinned?: boolean
    } = {},
) => {
    const { pinned } = options
    const user = await loadUserIfNeeded(slugOrId)

    if (!user) return

    const { id } = user

    updateRoomState(id, {
        isPinned: pinned,
        viewing: true,
    })

    setPinnedRoomSession(id, {
        open: pinned,
    })
}

export const openRoom = (
    slugOrId: string | number,
    options: {
        loungeSurrogateID?: number
        pinned?: boolean
        transitionSource?: TransitionType
    } = {},
) => {
    const [userSlug, userID] = getSlugAndId(slugOrId)
    const path =
        `/room/${userSlug}` +
        (options.loungeSurrogateID
            ? '/' + idToSlug[options.loungeSurrogateID]
            : '')
    routerPush(path, {
        transitionSource: options.transitionSource,
    })
    setPinnedRoomSession(userID, {
        open: options.pinned,
    })
}

type CloseRoomOptions = {
    destroy?: boolean
    skipCloseIfPinned?: boolean
}

export const closeRoom = (
    slugOrId: string | number,
    options: CloseRoomOptions = {},
) => {
    const [, id] = getSlugAndId(slugOrId)
    if (!id) return
    const { destroy, skipCloseIfPinned } = options

    if (destroy) {
        // unpin room
        pinRoom(id, { pinned: false, skipClose: true })
        // close any floating room
        closeFloatingRoom(id, { skipClose: true })
        // remove the room from localStorage
        forgetRoom(id)
    }

    const { pinnedRooms } = ROOMSTATE
    if (skipCloseIfPinned && pinnedRooms.includes(id)) return

    destroyRoomState(id)
}

export const requestClubShowInfo = (id: number) => {
    if (!id) return
    roomAction(RoomAction.CLUBSHOW_INFO, { id })
}

export const requestGroupShow = (slug: string) => {
    const id = slugToID[slug]
    if (!id) return
    roomAction(RoomAction.REQUEST_GROUP, { id })
}

export const requestPrivate = (slug: string) => {
    const id = slugToID[slug]
    if (!id) return
    roomAction(RoomAction.REQUEST_PRIVATE, { id })
}

export const requestVoyeur = (slug: string) => {
    const id = slugToID[slug]
    if (!id) return
    roomAction(RoomAction.REQUEST_VOYEUR, { id })
}

export const streamPublicShow = (id) => {
    // console.log(`[app-state: room ${id}] ATTEMPTING TO STREAM PUBLIC SHOW`)
    roomAction(RoomAction.STREAM_PUBLIC_SHOW, { id })
}

export const streamClubShow = (id) => {
    roomAction(RoomAction.STREAM_CLUBSHOW, { id })
}

export const setLoungeSurrogate = (
    loungeSurrogateID: number,
    loungeIdOrSlug: number | string,
    options?: { skipAddSession?: boolean; skipRoute?: boolean },
) => {
    const [, loungeId] = getSlugAndId(loungeIdOrSlug)
    const { skipAddSession, skipRoute = false, ...videoOptions } = options || {}
    initVideo(loungeSurrogateID, videoOptions)

    if (!skipAddSession) {
        addToLoungeRecentModelsSession(loungeSurrogateID)
    }

    startAutoPlayInterval(loungeId)
    if (!skipRoute) setLoungeSurrogateURL(loungeId, loungeSurrogateID)
    return loungeSurrogateID
}

export const setNextSurrogate = (loungeId: number, skipRoute = false) => {
    const { [loungeId]: room = {} } = getRoomState()
    const { loungeSurrogateID: previousSurrogate } = room
    const nextId = getSafeNextOrPreviousSurrogate(previousSurrogate, {
        shouldGet: true,
    })

    setLoungeSurrogate(nextId, loungeId, { skipRoute })

    return nextId
}

export const setPreviousSurrogate = (loungeId: number) => {
    const { [loungeId]: room = {} } = getRoomState()
    const { loungeSurrogateID: previousSurrogate } = room
    const previousId = getSafeNextOrPreviousSurrogate(previousSurrogate, {
        previous: true,
        shouldGet: true,
    })

    setLoungeSurrogate(previousId, loungeId)

    return previousId
}

export const stopAutoPlayInterval = () => {
    clearInterval(roomIntervals.autoPlay.interval)
}

export const startAutoPlayInterval = (loungeId: number) => {
    const { autoPlay, timer } = calculateLoungeSettings(
        SETTINGSSTATE[SettingsType.ROOM]?.loungeAutoplay,
    )
    stopAutoPlayInterval()
    if (autoPlay) {
        roomIntervals.autoPlay.interval = setInterval(() => {
            setNextSurrogate(loungeId)
        }, timer * 1000)
    }
    return () => stopAutoPlayInterval()
}

export const toggleAutoPlay = (loungeId: number) => {
    const { autoPlay } = calculateLoungeSettings(
        SETTINGSSTATE[SettingsType.ROOM]?.loungeAutoplay,
    )
    saveSettings(SettingsType.ROOM, 'loungeAutoplay', !autoPlay ? 0 : 1)
    if (autoPlay) {
        stopAutoPlayInterval()
    } else {
        startAutoPlayInterval(loungeId)
    }
}

export const togglePinRoomChat = (roomId: number) => {
    const room = getRoomState()[roomId]
    if (!room) return

    updateRoomState(roomId, {
        showChat: !room.showChat,
    })

    // ensure that we recalc the content area if this is portrait,
    // as toggling chat will change the pins container height
    const { portrait } = getScreenState()
    if (portrait) setScreenOrientation({ overrideSkip: true })
}

export const showPinRoomChat = (roomId: number, showChat = true) => {
    updateRoomState(roomId, {
        showChat,
    })
}

export const movePinnedRoom = (idToMove: number, indexToMoveTo: number) => {
    const { pinnedRooms } = ROOMSTATE
    const existingIndex = pinnedRooms.indexOf(idToMove)
    if (existingIndex >= 0) pinnedRooms.splice(existingIndex, 1)
    else pinRoom(idToMove)

    const roomsBefore = pinnedRooms.slice(0, indexToMoveTo)
    const roomsAfter = pinnedRooms.slice(indexToMoveTo)

    const newPinnedRooms = [].concat(roomsBefore, [idToMove], roomsAfter)

    setPinnedRooms(newPinnedRooms)
}

export const pinRoom = (
    roomId: number,
    { pinned = true, skipClose = false } = {},
) => {
    const { pinnedRooms } = ROOMSTATE
    // eslint-disable-next-line prefer-const
    let nextPinnedRooms = [...pinnedRooms]

    const rooms = getRoomState()
    if ((pinned && nextPinnedRooms.includes(roomId)) || rooms[roomId]?.isLounge)
        return

    // console.log('[pinRoom]', { roomId, pinned, skipClose })
    const { singlePin } = getScreenState()
    if (singlePin) {
        const [previousRoomId] = pinnedRooms
        nextPinnedRooms = []
        setPinnedRoomSession(previousRoomId, {
            open: false,
        })
        if (pinned) {
            closeFloatingPreview(roomId)
            nextPinnedRooms.push(roomId)
            openRoomState(roomId, { pinned: true })

            setRoomIDState(roomId)
            updateRoomState(roomId, {
                isPinned: true,
                viewing: true,
            })
        } else {
            setPinnedRoomSession(roomId, {
                open: false,
            })
        }

        if (!skipClose) {
            closeRoom(previousRoomId)
        } else {
            updateRoomState(roomId, {
                isPinned: false,
            })
        }
        setPinnedRooms(nextPinnedRooms)
        return
    }
    if (pinned) {
        toggleSidebarVisibility(false)
        closeFloatingPreview(roomId)
        nextPinnedRooms.push(roomId)

        openRoomState(roomId, { pinned: true })
        setRoomIDState(roomId)
        updateRoomState(roomId, {
            isPinned: true,
            viewing: true,
        })
        setPinnedRooms(nextPinnedRooms)
    } else {
        const index = nextPinnedRooms.indexOf(roomId)
        nextPinnedRooms.splice(index, 1)
        if (index >= 0) {
            setPinnedRoomSession(roomId, {
                open: false,
            })
        }
        if (!skipClose) {
            // check to see if we've got the room open, and auto skip if so
            if (
                !MODALSTATE.floating_room.show ||
                MODALSTATE.floating_room.state.id !== roomId
            )
                closeRoom(roomId)
            else
                updateRoomState(roomId, {
                    isPinned: false,
                })
        } else {
            updateRoomState(roomId, {
                isPinned: false,
            })
        }
        setPinnedRooms(nextPinnedRooms)
    }
}
